import React from "react";
import { ListCommandButtons, type ListCommandButtonsProps } from "./list-command-buttons";
import type { RepositoryStationaryUnits } from "src/domain/repositories/repository-stationary-units";
import { observer } from "mobx-react";
import { action, makeObservable } from "mobx";
import { store } from "../../store";
import type { UUID } from "src/utils/uuid";
import { defaultPageSize } from "src/utils/constants";
import type { ErrorReport } from "src/domain/services/service-errors";
import type { ResponseError } from "src/api";

export type ListCommandButtonsStationaryUnitsProps = Omit<
    ListCommandButtonsProps<RepositoryStationaryUnits>,
    "list" | "createForm" | "listState"
>;

@observer
export class ListCommandButtonsStationaryUnits extends React.Component<ListCommandButtonsStationaryUnitsProps> {
    private undeletableStationaryUnits: string[] = [];

    constructor(props: ListCommandButtonsStationaryUnitsProps) {
        super(props);
        makeObservable(this);
    }

    /** Tries to delete all stationary units and reports an error on
        encounter with a stationary units linked to anything.
    */
    @action.bound private async deleteStationaryUnits(ids: UUID[]): Promise<void> {
        await Promise.all(ids.map((id) => this.deleteSingleStationaryUnit(id)));

        // This reload is necessary to reload all queries that want to display "all" entities.
        await store.repositoryStationaryUnits.waitForIdle();
        await store.repositoryStationaryUnits.reloadQuery({ pageSize: defaultPageSize });

        // If we successfully deleted all stationary units return early
        if (this.undeletableStationaryUnits.length == 0) {
            return;
        }

        // Report error that lists all undeletable stationary units
        const stationaryUnitList = this.undeletableStationaryUnits.map((stationaryUnit) => (
            <li>{stationaryUnit}</li>
        ));
        const errorReport = {
            title: "component.errorBar.stationaryUnit.unableToDeleteStationaryUnit.title",
            description:
                "component.errorBar.stationaryUnit.unableToDeleteStationaryUnit.description",
            html: <ul>{stationaryUnitList}</ul>,
        } as ErrorReport;
        void store.serviceErrors.reportError(errorReport);
        this.undeletableStationaryUnits = [];
    }

    /** Tries to delete a single stationary unit. If we receive a validation error (422)
        we mark this stationary unit as undeletable. */
    private async deleteSingleStationaryUnit(id: UUID): Promise<void> {
        try {
            await store.stationaryUnitsApi.routesStationaryUnitsDeleteStationaryUnit({
                stationaryUnitId: id,
            });
            return;
        } catch (error) {
            const responseError = error as ResponseError;
            const response = responseError.response;

            // Keep track of stationary units that produce a validation error
            if (response.status === 422) {
                const stationary_unit = store.repositoryStationaryUnits.byId(id);
                this.undeletableStationaryUnits.push(
                    store.i18n.formatStationaryUnit(stationary_unit!),
                );
                return;
            } else {
                // We still have to report other errors (e.g., internal server error)
                store.serviceErrors.reportResponse(response);
            }
        }
    }

    public render(): JSX.Element {
        return (
            <ListCommandButtons
                listState={store.serviceListStates.stationaryUnits}
                {...this.props}
                canDelete
                onDelete={this.deleteStationaryUnits}
            />
        );
    }
}
